import { onSnapshot, collection, doc, setDoc, deleteDoc, writeBatch, getDocs, getDoc, updateDoc, runTransaction, query, where } from 'firebase/firestore';
import { db, auth } from '../../services/firebaseConfig';
import { COLLECTIONS } from '../../constants/collections';
import { safeGet, safeSet } from '../../utils/storage';
import { cartKey } from './keys';
import type { CartItem } from '../../types/cart';
import type { Product } from '../../types';
import { withErrorHandling, AppError, ErrorCode } from '../../lib/errors';
import { getAttr, findVariantByAttributes, getAvailableStock } from '../../utils/variant';
import type { ProductVariant } from '../../types';
import { sanitizeString } from '../../utils/sanitization';
import { isValidImageUrl } from '../../utils/imageValidation';
import { verifyUserIdOwnership } from '../../lib/security/idorProtection';

type Callback = (items: CartItem[]) => void;

export interface CartDriver {
  listen(cb: Callback): () => void;
  getAll(): Promise<CartItem[]>;
  upsert(item: CartItem): Promise<void>;
  remove(key: string): Promise<void>;
  setQuantity(key: string, quantity: number): Promise<void>;
  toggleSelected(key: string, selected: boolean): Promise<void>;
  clear(): Promise<void>;
}

// Local Storage driver (guest)
const LOCAL_KEY = 'cart_items_guest';

export function LocalCartDriver(): CartDriver {
  const read = () => safeGet<CartItem[]>(LOCAL_KEY, []);
  const write = (items: CartItem[]) => safeSet(LOCAL_KEY, items);
  return {
    listen(cb) { cb(read()); return () => {}; },
    async getAll() { return read(); },
    async upsert(item) {
      const key = cartKey({ productId: item.productId, variationId: item.variationId });
      const items = read();
      const idx = items.findIndex(i => cartKey({ productId: i.productId, variationId: i.variationId }) === key);
      if (idx >= 0) items[idx] = { ...items[idx], ...item };
      else items.push({ ...item, selected: item.selected ?? true });
      write(items);
    },
    async remove(key) {
      const items = read().filter(i => cartKey({ productId: i.productId, variationId: i.variationId }) !== key);
      write(items);
    },
    async setQuantity(key, quantity) {
      const items = read();
      const idx = items.findIndex(i => cartKey({ productId: i.productId, variationId: i.variationId }) === key);
      if (idx >= 0) { items[idx].quantity = quantity; write(items); }
    },
    async toggleSelected(key, selected) {
      const items = read();
      const idx = items.findIndex(i => cartKey({ productId: i.productId, variationId: i.variationId }) === key);
      if (idx >= 0) { items[idx].selected = selected; write(items); }
    },
    async clear() { write([]); }
  };
}

// Firestore driver (authed) - optimized for cache-first
// Firestore rule: allow list, get, create, update, delete: if authed() && (userId == uid() || isAdmin())
// Compliance: getUid() ensures user is authenticated and path uses authenticated user's uid
export function FirestoreCartDriver(): CartDriver {
  // Lazy initialization to avoid calling auth during module initialization
  const getUid = () => {
    const uid = auth.currentUser?.uid;
    if (!uid) {
      throw new Error('User must be authenticated to access Firestore cart');
    }
    return uid;
  };
  // Firestore rule compliance: Path is Users/{authenticatedUserId}/Cart
  // This ensures userId in path matches authenticated user's uid
  const getColRef = () => collection(db, `${COLLECTIONS.USERS}/${getUid()}/${COLLECTIONS.CART}`);
  
  return {
    listen(cb) {
      // Firestore rule: allow list, get - userId == uid() (enforced by path)
      // Only use realtime listener when cart drawer/page is open
      // This reduces unnecessary reads when cart is not being viewed
      const colRef = getColRef();
      return onSnapshot(colRef, snap => {
        const items: CartItem[] = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
        cb(items);
      });
    },
    async getAll() {
      // rely on listener; return empty
      return [];
    },
    async upsert(item) {
      // Firestore rule: allow create, update - userId == uid() (enforced by path)
      const uid = getUid();
      const key = cartKey({ productId: item.productId, variationId: item.variationId });
      const ref = doc(getColRef(), key);
      await setDoc(ref, { ...item, id: key, selected: item.selected ?? true, userId: uid }, { merge: true });
    },
    async remove(key) {
      // Firestore rule: allow delete - userId == uid() (enforced by path)
      // PERFORMANCE OPTIMIZATION: Direct delete without existence check
      // deleteDoc() doesn't throw for non-existent documents, so we can skip the expensive check
      // This eliminates 1-2 Firestore read operations per delete
      const ref = doc(getColRef(), key);
      await deleteDoc(ref);
      // Note: If the document doesn't exist, deleteDoc silently succeeds (no error thrown)
      // This is the desired behavior - idempotent delete operation
    },
    async setQuantity(key, quantity) {
      // Firestore rule: allow update - userId == uid() (enforced by updateCartItemQuantity)
      // Use the stock-validated update function for authenticated users
      // updateCartItemQuantity includes verifyUserIdOwnership check
      const uid = getUid();
      await updateCartItemQuantity(uid, key, quantity);
    },
    async toggleSelected(key, selected) {
      // Firestore rule: allow update - userId == uid() (enforced by path)
      await setDoc(doc(getColRef(), key), { selected }, { merge: true });
    },
    async clear() {
      // Firestore rule: allow delete - userId == uid() (enforced by path)
      // PERFORMANCE OPTIMIZATION: Batch delete all cart items efficiently
      // Note: We must read all items to delete them, but we batch the deletes for efficiency
      const colRef = getColRef();
      const snap = await getDocs(colRef);
      
      // Early exit if cart is already empty
      if (snap.empty) return;
      
      // Batch delete in chunks of 500 (Firestore batch limit)
      const MAX_BATCH_SIZE = 500;
      const docs = snap.docs;
      
      // Process in batches to handle large carts
      for (let i = 0; i < docs.length; i += MAX_BATCH_SIZE) {
        const batch = writeBatch(db);
        const batchDocs = docs.slice(i, i + MAX_BATCH_SIZE);
        batchDocs.forEach(d => batch.delete(d.ref));
        await batch.commit();
      }
    }
  };
}

export function createCartService(uid?: string | null): CartDriver {
  return uid ? FirestoreCartDriver() : LocalCartDriver();
}

// REMOVED: addToCartCallable - unused function

// Unified, typed add-to-cart service used by both Add to Cart and Buy Now
export type AddToCartInput = {
  userId: string;
  product: Product;
  quantity: number;
  selectedVariation?: {
    size?: string;
    color?: string;
    sku?: string;
  };
  source?: 'add_to_cart' | 'buy_now' | 'other';
};

export async function addToCart({ userId, product, quantity, selectedVariation, source = 'add_to_cart' }: AddToCartInput): Promise<CartItem> {
  return withErrorHandling(async () => {
    // Firestore rule: allow create - if authed() && (userId == uid() || isAdmin())
    // SECURITY: IDOR Protection - Verify userId matches authenticated user
    verifyUserIdOwnership(userId, {
      action: 'add items to cart',
      resourceType: 'cart'
    });

    // 2) Validate product data
    if (!product?.id || typeof product.id !== 'string' || product.id.trim().length === 0) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: 'Invalid product data'
      });
    }
    
    // 3) SECURITY: Strict quantity validation - must be positive integer within reasonable limits
    const MAX_QUANTITY = 100; // Maximum quantity per item
    const MIN_QUANTITY = 1;
    
    if (!Number.isFinite(quantity) || !Number.isInteger(quantity)) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: 'Quantity must be a whole number'
      });
    }
    
    if (quantity < MIN_QUANTITY || quantity > MAX_QUANTITY) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: `Quantity must be between ${MIN_QUANTITY} and ${MAX_QUANTITY}`
      });
    }

    // 4) SECURITY: Validate source parameter
    if (source && !['add_to_cart', 'buy_now', 'other'].includes(source)) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: 'Invalid source parameter'
      });
    }

    // Build selected attributes object
    // SECURITY: Sanitize variant attributes before use
    const selected: Record<string, string> = {};
    if (selectedVariation?.size) {
      const sanitizedSize = sanitizeString(selectedVariation.size, 50);
      if (sanitizedSize) selected["Size"] = sanitizedSize;
    }
    if (selectedVariation?.color) {
      const sanitizedColor = sanitizeString(selectedVariation.color, 50);
      if (sanitizedColor) selected["Color"] = sanitizedColor;
    }

    let resolvedSku = selectedVariation?.sku ?? "";
    let variant: ProductVariant | null = null;
    let stockQty = 0;

    if (product.productType === "variable") {
      // Check if variants actually have size/color data
      const variants = product.variants || [];
      const variantsWithSize = variants.filter(v => getAttr(v, 'Size') && getAttr(v, 'Size')!.trim() !== '');
      const variantsWithColor = variants.filter(v => getAttr(v, 'Color') && getAttr(v, 'Color')!.trim() !== '');
      
      // Only require size/color if variants actually have them
      if (variantsWithSize.length > 0 && !selectedVariation?.size) {
        throw new AppError({
          code: ErrorCode.VALIDATION_FAILED,
          domain: 'validation',
          message: `Size is required for this product. Available sizes: ${variantsWithSize.map(v => getAttr(v, 'Size')).join(', ')}`
        });
      }
      if (variantsWithColor.length > 0 && !selectedVariation?.color) {
        throw new AppError({
          code: ErrorCode.VALIDATION_FAILED,
          domain: 'validation',
          message: `Color is required for this product. Available colors: ${variantsWithColor.map(v => getAttr(v, 'Color')).join(', ')}`
        });
      }

      // Try to find exact variant match first
      variant = findVariantByAttributes(product, selected);
      
      // If no exact match, try to find any variant with stock
      if (!variant) {
        variant = (product.variants || []).find(v => 
          v.isActive !== false && (v.stockQty || 0) > 0
        ) || null;
      }
      
      // If still no variant, use the first available variant
      if (!variant && product.variants && product.variants.length > 0) {
        variant = product.variants[0];
      }
      
      if (!variant) {
        const availableVariants = (product.variants || []).map(v => 
          `${getAttr(v, 'Size') || 'One Size'}/${getAttr(v, 'Color') || 'Default'}`
        ).join(', ');
        
        throw new AppError({
          code: ErrorCode.VALIDATION_FAILED,
          domain: 'validation',
          message: `No valid variant found for this product. Available variants: ${availableVariants || 'None'}`
        });
      }
      
      stockQty = variant.stockQty || 0;
      resolvedSku = resolvedSku || variant.sku || "";
      
      if (stockQty <= 0) {
        throw new AppError({
          code: ErrorCode.VALIDATION_FAILED,
          domain: 'validation',
          message: 'Selected variant is out of stock.'
        });
      }
    } else {
      // simple product – no variant required
      stockQty = getAvailableStock(product);
      if (stockQty <= 0) {
        throw new AppError({
          code: ErrorCode.VALIDATION_FAILED,
          domain: 'validation',
          message: 'This product is out of stock.'
        });
      }
    }

    // quantity guard
    if (quantity > stockQty) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: 'Selected quantity exceeds available stock.'
      });
    }

    // CRITICAL FIX: Always prioritize product discountPrice
    // Price resolution logic (ensures discounted prices are always used):
    // 1. Check if product has discountPrice first (this is the main discounted price)
    // 2. For variable products: variant.price or variant.priceOverride (if exists and is variant-specific discount)
    // 3. Fallback: product.price
    let unitPrice: number;
    
    // Always check product discountPrice first
    if (typeof product.discountPrice === 'number' && product.discountPrice > 0) {
      // Product has discountPrice - use it unless variant has its own discounted price
      if (product.productType === "variable" && variant) {
        const variantPrice = (variant as any)?.price ?? variant?.priceOverride;
        // Only use variant price if it's different from original AND different from discountPrice
        // (meaning variant has its own specific discount)
        if (typeof variantPrice === 'number' && variantPrice >= 0 && 
            variantPrice !== product.price && variantPrice !== product.discountPrice) {
          unitPrice = variantPrice; // Variant has unique discounted price
        } else {
          unitPrice = product.discountPrice; // Use product discountPrice
        }
      } else {
        unitPrice = product.discountPrice; // Simple product
      }
    } else {
      // No product discountPrice, check variant price for variable products
      if (product.productType === "variable" && variant) {
        const variantPrice = (variant as any)?.price ?? variant?.priceOverride;
        if (typeof variantPrice === 'number' && variantPrice >= 0) {
          unitPrice = variantPrice;
        } else {
          unitPrice = product.price;
        }
      } else {
        // Simple product without discountPrice
        unitPrice = product.price;
      }
    }

    // Build cart item key and payload
    const variationId = variant ? JSON.stringify(variant.attributeValues ?? {}) : 'simple';
    const key = cartKey({ productId: product.id, variationId });

    // Build base item - only include weight if it's a valid number
    // CRITICAL FIX: For "Buy Now", always set selected to true to ensure item is included in checkout
    // For regular "Add to Cart", default to true (don't set undefined as Firestore doesn't allow it)
    const baseItem: CartItem = {
      id: key,
      productId: product.id,
      variationId: variationId,
      title: product.title,
      price: unitPrice,
      quantity,
      selectedVariation: {
        size: selected["Size"] || 'One Size',
        color: selected["Color"] || 'Default',
        sku: resolvedSku,
      },
      image: (variant?.image && isValidImageUrl(variant.image)) ? variant.image : product.images?.[0] || '',
      sellerId: product.sellerId,
      selected: true, // Default to true for all new items (Firestore doesn't allow undefined)
      ...(product.weight && typeof product.weight === 'number' && product.weight > 0 ? { weight: product.weight } : {}),
    };

    // 4) Use transaction for atomic stock check and cart update
    const cartDoc = doc(db, 'Users', userId, 'Cart', key);
    const productDoc = doc(db, 'Products', product.id);
    const cartCollection = collection(db, 'Users', userId, 'Cart');

    // PERFORMANCE OPTIMIZATION: For "Buy Now", only deselect items that are actually selected
    // Use a query to only fetch selected items instead of reading the entire cart
    // This dramatically reduces read operations and improves performance
    // NOTE: Since all new items have selected: true (line 392), we only need to query for selected items
    // Legacy items without the field are treated as unselected by checkout (line 178 in CheckoutPage.tsx)
    if (source === 'buy_now') {
      try {
        // CRITICAL OPTIMIZATION: Only query for items that are explicitly selected (true)
        // This avoids reading all cart items - only reads items that need to be deselected
        // Firestore queries with where() are much faster than reading entire collections
        const selectedItemsQuery = query(
          cartCollection,
          where('selected', '==', true)
        );
        const selectedItemsSnapshot = await getDocs(selectedItemsQuery);
        
        // If no selected items found, nothing to deselect - skip batch operation entirely
        if (selectedItemsSnapshot.empty) {
          // No items to deselect, we're done - this is the fastest path
          // The transaction below will handle adding/selecting the buy now item
        } else {
          // Only deselect items that are explicitly selected (true)
          // This is typically 1-5 items, not the entire cart
          const batch = writeBatch(db);
          let batchCount = 0;
          const MAX_BATCH_SIZE = 500; // Firestore batch limit
          
          selectedItemsSnapshot.docs.forEach((cartItemDoc) => {
            if (batchCount >= MAX_BATCH_SIZE) return;
            
            const cartItemId = cartItemDoc.id;
            // Skip invalid IDs or the item we're about to add (it will be selected in transaction)
            if (!cartItemId || cartItemId.length > 1500 || cartItemId === key) {
              return;
            }
            
            const otherCartDoc = doc(db, 'Users', userId, 'Cart', cartItemId);
            batch.update(otherCartDoc, { 
              selected: false,
              updatedAt: new Date()
            });
            batchCount++;
          });
          
          // Only commit if we have items to update
          if (batchCount > 0) {
            await batch.commit();
          }
        }
        
        // NOTE: We intentionally skip checking for items with undefined selected field because:
        // 1. All new items have selected: true (line 392)
        // 2. CheckoutPage only includes items with selected === true (line 178)
        // 3. Legacy items without the field are effectively treated as unselected
        // 4. This avoids the expensive full collection read that was slowing things down
      } catch (batchError) {
        // SECURITY: Log but don't fail the entire operation if batch deselect fails
        // The transaction will still ensure the buy now item is selected
        // SECURITY: Removed console.warn to prevent information leakage
        // Continue with the transaction - the buy now item will still be added/selected
      }
    }

    // PERFORMANCE: Cache variant lookup helper to avoid repeated iterations
    // ProductVariant doesn't have an id field, so we match by attributes
    const findVariantInProduct = (productData: Product): ProductVariant | null => {
      if (productData.productType !== "variable" || !variant) return null;
      const variants = productData.variants || [];
      // Match by attributes (optimized - only iterates once)
      const variantSize = selected["Size"] || 'One Size';
      const variantColor = selected["Color"] || 'Default';
      return variants.find(v => {
        const vSize = getAttr(v, 'Size') || 'One Size';
        const vColor = getAttr(v, 'Color') || 'Default';
        return vSize === variantSize && vColor === variantColor;
      }) || null;
    };

    return await runTransaction(db, async (transaction) => {
      // Read current cart item and product in transaction
      const existingCartDoc = await transaction.get(cartDoc);
      const currentProductDoc = await transaction.get(productDoc);

      if (!currentProductDoc.exists()) {
        throw new AppError({
          code: ErrorCode.DB_NOT_FOUND,
          domain: 'validation',
          message: 'Product not found'
        });
      }

      const currentProduct = currentProductDoc.data() as Product;

      // PERFORMANCE: Re-validate stock in transaction using cached variant lookup
      let currentStockQty = 0;
      let currentVariant: ProductVariant | null = null;
      
      if (currentProduct.productType === "variable") {
        // Use optimized variant lookup
        currentVariant = findVariantInProduct(currentProduct);
        
        if (!currentVariant) {
          throw new AppError({
            code: ErrorCode.VALIDATION_FAILED,
            domain: 'validation',
            message: 'Selected variant no longer available'
          });
        }
        currentStockQty = currentVariant.stockQty || 0;
      } else {
        currentStockQty = getAvailableStock(currentProduct);
      }

      if (currentStockQty <= 0) {
        throw new AppError({
          code: ErrorCode.VALIDATION_FAILED,
          domain: 'validation',
          message: 'Product is out of stock'
        });
      }

      if (existingCartDoc.exists()) {
        // Update existing item
        const existingData = existingCartDoc.data() as CartItem;
        
        // SECURITY: Validate existing cart item data
        const existingQuantity = typeof existingData.quantity === 'number' && Number.isInteger(existingData.quantity) 
          ? existingData.quantity 
          : 0;
        
        if (existingQuantity < 0 || existingQuantity > 100) {
          throw new AppError({
            code: ErrorCode.VALIDATION_FAILED,
            domain: 'validation',
            message: 'Invalid existing cart item quantity'
          });
        }

        // CRITICAL FIX: For "Buy Now", REPLACE the quantity instead of adding to it
        // For regular "Add to Cart", ADD to existing quantity
        // SECURITY: Ensure quantity calculations are safe and within limits
        let newQuantity: number;
        if (source === 'buy_now') {
          // "Buy Now" replaces the quantity with the selected quantity
          // This ensures user gets exactly what they clicked "Buy Now" for
          newQuantity = quantity;
        } else {
          // Regular "Add to Cart" adds to existing quantity
          // SECURITY: Prevent integer overflow by checking sum before assignment
          const sum = existingQuantity + quantity;
          if (sum > 100) {
            throw new AppError({
              code: ErrorCode.VALIDATION_FAILED,
              domain: 'validation',
              message: `Cannot add more items. Maximum quantity per item is 100. Current: ${existingQuantity}, Adding: ${quantity}`
            });
          }
          newQuantity = sum;
        }
        
        // SECURITY: Final validation - ensure new quantity is valid integer
        if (!Number.isInteger(newQuantity) || newQuantity < 1 || newQuantity > 100) {
          throw new AppError({
            code: ErrorCode.VALIDATION_FAILED,
            domain: 'validation',
            message: 'Invalid calculated quantity'
          });
        }

        // SECURITY: Stock validation - must check against current stock in transaction
        if (newQuantity > currentStockQty) {
          const action = source === 'buy_now' ? 'purchase' : 'add';
          throw new AppError({
            code: ErrorCode.VALIDATION_FAILED,
            domain: 'validation',
            message: `Cannot ${action} ${newQuantity} items. Only ${currentStockQty} available in stock.`
          });
        }

        // CRITICAL FIX: Handle selected state properly
        // - For "Buy Now": always ensure item is selected (re-select if it was unselected from failed order)
        // - For regular "Add to Cart": preserve existing selected state, but if unselected, re-select it (user is actively adding it)
        let newSelectedState: boolean;
        if (source === 'buy_now') {
          // "Buy Now" always selects the item
          newSelectedState = true;
        } else {
          // Regular "Add to Cart": if item was unselected (from failed order), re-select it since user is actively adding
          // Otherwise preserve the existing state (default to true if undefined)
          newSelectedState = existingData.selected === false ? true : (existingData.selected ?? true);
        }

        // SECURITY: Re-validate price hasn't changed (prevent price manipulation)
        // PERFORMANCE: Reuse the currentVariant we already found above
        let finalPrice = existingData.price;
        if (currentProduct.productType === "variable" && currentVariant) {
          const variantPrice = (currentVariant as any)?.price ?? currentVariant?.priceOverride;
          if (typeof variantPrice === 'number' && variantPrice >= 0) {
            finalPrice = variantPrice;
          } else {
            finalPrice = (typeof currentProduct.discountPrice === 'number' && currentProduct.discountPrice > 0)
              ? currentProduct.discountPrice
              : currentProduct.price;
          }
        } else {
          finalPrice = (typeof currentProduct.discountPrice === 'number' && currentProduct.discountPrice > 0)
            ? currentProduct.discountPrice
            : currentProduct.price;
        }

        // SECURITY: Validate price is valid number
        if (!Number.isFinite(finalPrice) || finalPrice < 0 || finalPrice > 10000000) {
          throw new AppError({
            code: ErrorCode.VALIDATION_FAILED,
            domain: 'validation',
            message: 'Invalid product price'
          });
        }

        // Update cart item atomically within transaction
        transaction.update(cartDoc, {
          quantity: newQuantity,
          price: finalPrice, // Update price in case it changed
          addedAt: new Date(),
          updatedAt: new Date(),
          selected: newSelectedState, // Always set a boolean value (Firestore doesn't allow undefined)
        });

        const result = { 
          ...existingData, 
          quantity: newQuantity, 
          price: finalPrice,
          selected: newSelectedState 
        };
        return result;
      } else {
        // SECURITY: Validate quantity before creating new item
        if (!Number.isInteger(quantity) || quantity < 1 || quantity > 100) {
          throw new AppError({
            code: ErrorCode.VALIDATION_FAILED,
            domain: 'validation',
            message: 'Invalid quantity for new cart item'
          });
        }

        if (quantity > currentStockQty) {
          throw new AppError({
            code: ErrorCode.VALIDATION_FAILED,
            domain: 'validation',
            message: `Selected quantity exceeds available stock. Only ${currentStockQty} available.`
          });
        }

        // SECURITY: Final validation of baseItem before creating
        if (!baseItem.productId || !baseItem.sellerId || !Number.isFinite(baseItem.price) || baseItem.price < 0) {
          throw new AppError({
            code: ErrorCode.VALIDATION_FAILED,
            domain: 'validation',
            message: 'Invalid cart item data'
          });
        }

        // Create new item atomically within transaction
        transaction.set(cartDoc, {
          ...baseItem,
          addedAt: new Date(),
        });
        return { ...baseItem, addedAt: new Date() };
      }
    });
  }, {
    ctx: { feature: 'cart', action: 'addToCart', userId, extra: { productId: product?.id } },
    domainHint: 'firestore'
  });
}

/**
 * Get cart items for a user
 */
export async function getCartItems(userId: string): Promise<CartItem[]> {
  return withErrorHandling(async () => {
    // SECURITY: IDOR Protection - Verify userId matches authenticated user
    verifyUserIdOwnership(userId, {
      action: 'view cart',
      resourceType: 'cart'
    });
    
    const cartCollection = collection(db, 'Users', userId, 'Cart');
    const querySnapshot = await getDocs(cartCollection);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as CartItem[];
  }, {
    ctx: { feature: 'cart', action: 'getCartItems', userId },
    domainHint: 'firestore'
  });
}

/**
 * Update cart item quantity with optimized stock validation
 * Uses cached product data to minimize Firebase reads
 */
export async function updateCartItemQuantity(userId: string, itemId: string, quantity: number, cachedProduct?: Product): Promise<void> {
  return withErrorHandling(async () => {
    // Firestore rule: allow update - if authed() && (userId == uid() || isAdmin())
    // SECURITY: IDOR Protection - Verify userId matches authenticated user
    verifyUserIdOwnership(userId, {
      action: 'update cart',
      resourceType: 'cart'
    });
    
    if (quantity <= 0) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: 'Quantity must be greater than 0'
      });
    }

    // Get current cart item (single read)
    const cartDoc = doc(db, 'Users', userId, 'Cart', itemId);
    const cartItemDoc = await getDoc(cartDoc);
    
    if (!cartItemDoc.exists()) {
      throw new AppError({
        code: ErrorCode.DB_NOT_FOUND,
        domain: 'validation',
        message: 'Cart item not found'
      });
    }

    const cartItem = cartItemDoc.data() as CartItem;
    
    // Use cached product data if available, otherwise fetch (optimization)
    let product: Product;
    if (cachedProduct && cachedProduct.id === cartItem.productId) {
      product = cachedProduct;
    } else {
      const productDoc = await getDoc(doc(db, 'Products', cartItem.productId));
      if (!productDoc.exists()) {
        throw new AppError({
          code: ErrorCode.DB_NOT_FOUND,
          domain: 'validation',
          message: 'Product not found'
        });
      }
      product = productDoc.data() as Product;
    }

    let availableStock = 0;

    // Check stock based on product type
    if (product.productType === 'variable') {
      // Find the variant that matches the cart item's selected variation
      const selectedVariation = cartItem.selectedVariation;
      const variant = product.variants?.find(v => {
        const variantSize = v.attributeValues?.Size || 'One Size';
        const variantColor = v.attributeValues?.Color || 'Default';
        return variantSize === selectedVariation?.size && variantColor === selectedVariation?.color;
      });
      
      if (!variant) {
        throw new AppError({
          code: ErrorCode.VALIDATION_FAILED,
          domain: 'validation',
          message: 'Selected variant no longer available'
        });
      }
      
      availableStock = variant.stockQty || 0;
    } else {
      availableStock = getAvailableStock(product);
    }

    // Validate quantity against available stock
    if (quantity > availableStock) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: `Only ${availableStock} items available in stock`
      });
    }

    // Update cart item quantity atomically using transaction
    // This prevents race conditions when multiple updates happen simultaneously
    await runTransaction(db, async (tx) => {
      // Re-read cart item in transaction to ensure consistency
      const cartSnap = await tx.get(cartDoc);
      if (!cartSnap.exists()) {
        throw new AppError({
          code: ErrorCode.DB_NOT_FOUND,
          domain: 'validation',
          message: 'Cart item not found'
        });
      }

      const currentCartItem = cartSnap.data() as CartItem;

      // Re-read product in transaction to ensure stock is current
      const productDocRef = doc(db, 'Products', currentCartItem.productId);
      const productSnap = await tx.get(productDocRef);
      if (!productSnap.exists()) {
        throw new AppError({
          code: ErrorCode.DB_NOT_FOUND,
          domain: 'validation',
          message: 'Product not found'
        });
      }

      const currentProduct = productSnap.data() as Product;
      
      // Re-validate stock in transaction
      let currentAvailableStock = 0;
      if (currentProduct.productType === 'variable') {
        const selectedVariation = currentCartItem.selectedVariation;
        const variant = currentProduct.variants?.find(v => {
          const variantSize = v.attributeValues?.Size || 'One Size';
          const variantColor = v.attributeValues?.Color || 'Default';
          return variantSize === selectedVariation?.size && variantColor === selectedVariation?.color;
        });
        currentAvailableStock = variant?.stockQty || 0;
      } else {
        currentAvailableStock = getAvailableStock(currentProduct);
      }

      // Final stock validation in transaction
      if (quantity > currentAvailableStock) {
        throw new AppError({
          code: ErrorCode.VALIDATION_FAILED,
          domain: 'validation',
          message: `Only ${currentAvailableStock} items available in stock`
        });
      }

      // Update quantity atomically
      tx.update(cartDoc, {
        quantity,
        updatedAt: new Date(),
      });
    });
  }, {
    ctx: { feature: 'cart', action: 'updateCartItemQuantity', userId, extra: { itemId } },
    domainHint: 'firestore'
  });
}

/**
 * Fast cart quantity update using local stock validation
 * No Firebase reads - uses cached stock data for instant updates
 */
export async function fastUpdateCartQuantity(
  userId: string, 
  itemId: string, 
  newQuantity: number,
  availableStock: number,
  onLocalUpdate: (itemId: string, quantity: number) => void,
  onError: (error: string) => void
): Promise<void> {
  // Validate against local stock data (instant)
  if (newQuantity > availableStock) {
    onError(`Only ${availableStock} items available in stock`);
    return;
  }

  if (newQuantity <= 0) {
    onError('Quantity must be greater than 0');
    return;
  }

  // Immediate local update for instant UI response
  onLocalUpdate(itemId, newQuantity);
  
  // Background Firebase sync (single write operation)
  try {
    const cartDoc = doc(db, 'Users', userId, 'Cart', itemId);
    await updateDoc(cartDoc, {
      quantity: newQuantity,
      updatedAt: new Date()
    });
  } catch (error: any) {
    // Log sync conflicts for debugging
    // SECURITY: Removed console.error to prevent information leakage
    
    // Revert local update on error
    const cartDoc = doc(db, 'Users', userId, 'Cart', itemId);
    const cartItemDoc = await getDoc(cartDoc);
    if (cartItemDoc.exists()) {
      const currentQuantity = cartItemDoc.data()?.quantity || 0;
      onLocalUpdate(itemId, currentQuantity);
    }
    onError(error?.message || 'Failed to update quantity');
  }
}

/**
 * Check if a cart item quantity can be updated to the specified amount
 */
export async function canUpdateCartItemQuantity(userId: string, itemId: string, newQuantity: number): Promise<{ canUpdate: boolean; availableStock: number; message?: string }> {
  return withErrorHandling(async () => {
    if (newQuantity <= 0) {
      return { canUpdate: false, availableStock: 0, message: 'Quantity must be greater than 0' };
    }

    // Get current cart item
    const cartDoc = doc(db, 'Users', userId, 'Cart', itemId);
    const cartItemDoc = await getDoc(cartDoc);
    
    if (!cartItemDoc.exists()) {
      return { canUpdate: false, availableStock: 0, message: 'Cart item not found' };
    }

    const cartItem = cartItemDoc.data() as CartItem;
    
    // Fetch current product data to check stock
    const productDoc = await getDoc(doc(db, 'Products', cartItem.productId));
    if (!productDoc.exists()) {
      return { canUpdate: false, availableStock: 0, message: 'Product not found' };
    }

    const product = productDoc.data() as Product;
    let availableStock = 0;

    // Check stock based on product type
    if (product.productType === 'variable') {
      const selectedVariation = cartItem.selectedVariation;
      const variant = product.variants?.find(v => {
        const variantSize = v.attributeValues?.Size || 'One Size';
        const variantColor = v.attributeValues?.Color || 'Default';
        return variantSize === selectedVariation?.size && variantColor === selectedVariation?.color;
      });
      
      if (!variant) {
        return { canUpdate: false, availableStock: 0, message: 'Selected variant no longer available' };
      }
      
      availableStock = variant.stockQty || 0;
    } else {
      availableStock = getAvailableStock(product);
    }

    if (newQuantity > availableStock) {
      return { 
        canUpdate: false, 
        availableStock, 
        message: `Only ${availableStock} items available in stock` 
      };
    }

    return { canUpdate: true, availableStock };
  }, {
    ctx: { feature: 'cart', action: 'canUpdateCartItemQuantity', userId, extra: { itemId } },
    domainHint: 'firestore'
  });
}

/**
 * Remove item from cart
 */
export async function removeFromCart(userId: string, itemId: string): Promise<void> {
  return withErrorHandling(async () => {
    // Firestore rule: allow delete - if authed() && (userId == uid() || isAdmin())
    // SECURITY: IDOR Protection - Verify userId matches authenticated user
    verifyUserIdOwnership(userId, {
      action: 'remove items from cart',
      resourceType: 'cart'
    });
    
    const cartDoc = doc(db, 'Users', userId, 'Cart', itemId);
    
    await deleteDoc(cartDoc);
    
  }, {
    ctx: { feature: 'cart', action: 'removeFromCart', userId, extra: { itemId } },
    domainHint: 'firestore'
  });
}

/**
 * Clear entire cart
 */
export async function clearCart(userId: string): Promise<void> {
  return withErrorHandling(async () => {
    // Firestore rule: allow delete - if authed() && (userId == uid() || isAdmin())
    // SECURITY: IDOR Protection - Verify userId matches authenticated user
    verifyUserIdOwnership(userId, {
      action: 'clear cart',
      resourceType: 'cart'
    });
    
    // PERFORMANCE OPTIMIZATION: Batch delete all cart items efficiently
    // Process in chunks to handle large carts (Firestore batch limit is 500)
    const cartCollection = collection(db, 'Users', userId, 'Cart');
    const querySnapshot = await getDocs(cartCollection);
    
    // Early exit if cart is already empty
    if (querySnapshot.empty) return;
    
    const MAX_BATCH_SIZE = 500;
    const docs = querySnapshot.docs;
    
    // Process in batches to handle large carts efficiently
    for (let i = 0; i < docs.length; i += MAX_BATCH_SIZE) {
      const batch = writeBatch(db);
      const batchDocs = docs.slice(i, i + MAX_BATCH_SIZE);
      batchDocs.forEach(doc => batch.delete(doc.ref));
      await batch.commit();
    }
    
  }, {
    ctx: { feature: 'cart', action: 'clearCart', userId },
    domainHint: 'firestore'
  });
}

/**
 * Get cart item by ID
 */
export async function getCartItem(userId: string, itemId: string): Promise<CartItem | null> {
  return withErrorHandling(async () => {
    // SECURITY: IDOR Protection - Verify userId matches authenticated user
    verifyUserIdOwnership(userId, {
      action: 'view cart items',
      resourceType: 'cart'
    });
    
    const cartDoc = doc(db, 'Users', userId, 'Cart', itemId);
    const docSnap = await getDoc(cartDoc);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      } as CartItem;
    }
    
    return null;
  }, {
    ctx: { feature: 'cart', action: 'getCartItem', userId, extra: { itemId } },
    domainHint: 'firestore'
  });
}

/**
 * Get cart summary (total items, total price)
 */
export async function getCartSummary(userId: string): Promise<{ totalItems: number; totalPrice: number }> {
  return withErrorHandling(async () => {
    // SECURITY: IDOR Protection - getCartItems will validate ownership
    const items = await getCartItems(userId);
    
    const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
    const totalPrice = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    return { totalItems, totalPrice };
  }, {
    ctx: { feature: 'cart', action: 'getCartSummary', userId },
    domainHint: 'firestore'
  });
}

/**
 * Validate and clean up cart items - removes products that no longer exist or are unavailable
 * Returns list of invalid item IDs that should be removed and items that need quantity adjustment
 */
export async function validateAndCleanupCartItems(
  userId: string, 
  items: CartItem[]
): Promise<{ invalidItemIds: string[]; quantityAdjustments: Array<{ itemId: string; newQuantity: number }> }> {
  // SECURITY: IDOR Protection - Verify userId matches authenticated user
  verifyUserIdOwnership(userId, {
    action: 'validate cart',
    resourceType: 'cart'
  });
  
  const invalidItemIds: string[] = [];
  const quantityAdjustments: Array<{ itemId: string; newQuantity: number }> = [];
  
  if (!items || items.length === 0) {
    return { invalidItemIds, quantityAdjustments };
  }

  try {
    // Check each cart item
    for (const item of items) {
      try {
        // Check if product exists
        const productDoc = await getDoc(doc(db, 'Products', item.productId));
        
        if (!productDoc.exists()) {
          // Product no longer exists - remove it
          // Always use item.id (Firestore document ID) if available, otherwise fallback to cartKey
          if (!item.id) {
            // SECURITY: Removed console.warn to prevent information leakage
          }
          invalidItemIds.push(item.id || cartKey({ productId: item.productId, variationId: item.variationId }));
          continue;
        }

        const product = productDoc.data() as Product;
        
        // Check if product is available (status must be approved)
        if (product.status !== 'approved') {
          // Product not approved - remove it
          // Always use item.id (Firestore document ID) if available
          if (!item.id) {
            // SECURITY: Removed console.warn to prevent information leakage
          }
          invalidItemIds.push(item.id || cartKey({ productId: item.productId, variationId: item.variationId }));
          continue;
        }

        // For variable products, check if variant still exists and is available
        if (product.productType === 'variable') {
          const selectedVariation = item.selectedVariation;
          const variant = product.variants?.find(v => {
            const variantSize = v.attributeValues?.Size || 'One Size';
            const variantColor = v.attributeValues?.Color || 'Default';
            return variantSize === selectedVariation?.size && variantColor === selectedVariation?.color;
          });
          
          if (!variant) {
            // Variant no longer exists - remove it
            // Always use item.id (Firestore document ID) if available
            if (!item.id) {
              // SECURITY: Removed console.warn to prevent information leakage
            }
            invalidItemIds.push(item.id || cartKey({ productId: item.productId, variationId: item.variationId }));
            continue;
          }

          // Check if variant has stock
          const availableStock = variant.stockQty || 0;
          if (availableStock <= 0) {
            // Variant is out of stock - remove it
            // Always use item.id (Firestore document ID) if available
            if (!item.id) {
              // SECURITY: Removed console.warn to prevent information leakage
            }
            invalidItemIds.push(item.id || cartKey({ productId: item.productId, variationId: item.variationId }));
            continue;
          }

          // Check if requested quantity exceeds available stock - adjust instead of removing
          if (item.quantity > availableStock) {
            // Adjust quantity to available stock instead of removing
            // Always use item.id (Firestore document ID) if available
            if (!item.id) {
              // SECURITY: Removed console.warn to prevent information leakage
            }
            quantityAdjustments.push({
              itemId: item.id || cartKey({ productId: item.productId, variationId: item.variationId }),
              newQuantity: availableStock
            });
            continue;
          }
        } else {
          // Simple product - check stock
          const availableStock = getAvailableStock(product);
          if (availableStock <= 0) {
            // Product is out of stock - remove it
            // Always use item.id (Firestore document ID) if available
            if (!item.id) {
              // SECURITY: Removed console.warn to prevent information leakage
            }
            invalidItemIds.push(item.id || cartKey({ productId: item.productId, variationId: item.variationId }));
            continue;
          }

          // Check if requested quantity exceeds available stock - adjust instead of removing
          if (item.quantity > availableStock) {
            // Adjust quantity to available stock instead of removing
            // Always use item.id (Firestore document ID) if available
            if (!item.id) {
              // SECURITY: Removed console.warn to prevent information leakage
            }
            quantityAdjustments.push({
              itemId: item.id || cartKey({ productId: item.productId, variationId: item.variationId }),
              newQuantity: availableStock
            });
            continue;
          }
        }
      } catch (error) {
        // If we can't validate the item due to network/error, DON'T remove it
        // Only log the error and skip this item to avoid removing valid items on temporary errors
        // SECURITY: Removed console.warn to prevent information leakage
        // Do NOT add to invalidItemIds - let the item remain if we can't validate
        continue;
      }
    }

    // Apply quantity adjustments first (before removing items)
    if (quantityAdjustments.length > 0) {
      const batch = writeBatch(db);
      for (const adjustment of quantityAdjustments) {
        const cartDoc = doc(db, 'Users', userId, 'Cart', adjustment.itemId);
        batch.update(cartDoc, { quantity: adjustment.newQuantity, updatedAt: new Date() });
      }
      await batch.commit();
    }

    // Remove invalid items from cart (only items that are definitively invalid)
    if (invalidItemIds.length > 0) {
      const batch = writeBatch(db);
      for (const itemId of invalidItemIds) {
        const cartDoc = doc(db, 'Users', userId, 'Cart', itemId);
        batch.delete(cartDoc);
      }
      await batch.commit();
    }
  } catch (error) {
    // SECURITY: Removed console.error to prevent information leakage
    // Don't throw - let items remain if validation fails
    // Return empty arrays to prevent any removals on error
    return { invalidItemIds: [], quantityAdjustments: [] };
  }

  return { invalidItemIds, quantityAdjustments };
}



